import React, { useState } from "react";
import { Save, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Field, OLIVE, PageHead, errMsg } from "../components/dpa/dpaUtils";
import { MfaCard } from "../components/MfaCard";

const ProfileCard = ({ user, onSaved }) => {
  const [name, setName] = useState(user.name || "");
  const save = async () => {
    try { const { data } = await api.put("/auth/me", { name }); onSaved(data); toast.success("Name updated"); }
    catch (e) { toast.error("Could not save", { description: errMsg(e) }); }
  };
  return (
    <Card className="rounded-none p-6 space-y-4" data-testid="account-profile-card">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Profile</div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Name" required value={name} onChange={setName} testId="account-name-input" />
        <Field label="Email" value={user.email} disabled testId="account-email" hint="Ask an administrator to change your email" />
        <Field label="Role" value={user.role.replace("_", " ")} disabled testId="account-role" />
      </div>
      <Button className="rounded-none text-white" style={OLIVE} disabled={!name.trim()} onClick={save} data-testid="account-save-name-btn"><Save className="h-4 w-4 mr-1" />Save name</Button>
    </Card>
  );
};

const PasswordCard = () => {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const mismatch = f.confirm && f.new_password !== f.confirm;
  const ready = f.current_password && f.new_password.length >= 8 && f.new_password === f.confirm;
  const save = async () => {
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      localStorage.setItem("epc_token", data.token);
      setF({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed", { description: "You've been signed out everywhere else. Use your new password next time." });
    } catch (e) { toast.error("Password not changed", { description: errMsg(e) }); }
  };
  return (
    <Card className="rounded-none p-6 space-y-4" data-testid="account-password-card">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Change password</div>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Current password" type="password" required value={f.current_password} onChange={(v) => setF({ ...f, current_password: v })} testId="account-current-password" />
        <Field label="New password" type="password" required value={f.new_password} onChange={(v) => setF({ ...f, new_password: v })} testId="account-new-password" hint="At least 8 characters" />
        <Field label="Confirm new password" type="password" required value={f.confirm} onChange={(v) => setF({ ...f, confirm: v })} testId="account-confirm-password" hint={mismatch ? "Passwords don't match" : ""} />
      </div>
      <Button className="rounded-none text-white" style={OLIVE} disabled={!ready} onClick={save} data-testid="account-change-password-btn"><KeyRound className="h-4 w-4 mr-1" />Change password</Button>
    </Card>
  );
};

export default function AccountPage() {
  const { user, setUserAfterLogin } = useAuth();
  if (!user) return null;
  return (
    <div className="p-8 space-y-6 fade-up max-w-4xl" data-testid="account-page">
      <PageHead kicker="Signed in" title="My account" sub="Update your name or change your password. Your role is managed by an administrator." />
      <ProfileCard user={user} onSaved={(u) => setUserAfterLogin({ ...user, ...u })} />
      <PasswordCard />
      <MfaCard />
    </div>
  );
}
